import React, { useEffect, useState } from 'react';
import { Loader2, Mail, Send, ShieldCheck } from 'lucide-react';
import {
  BACKUP_FREQUENCY_LABELS,
  fetchBackupSettings,
  sendBackupNow,
  updateBackupSettings,
  type BackupFormat,
  type BackupFrequency,
  type BackupSettings as Settings,
} from '../services/backupApi';
import { describeError } from '../utils/describeError';

const ROLE_SCOPE: Record<string, string> = {
  ADMINISTRADOR: 'todos os dados da clínica (pacientes, prontuários, ciclos, agenda e livro caixa)',
  PROFISSIONAL: 'apenas os seus pacientes (cadastro, prontuário, ciclos) e a sua agenda',
  RECEPCIONISTA: 'cadastro dos pacientes, agenda e livro caixa — sem prontuários nem dados clínicos',
};

/**
 * Bloco 37: seção "Backup Automático" em Configurações — ativar/desativar,
 * formato (PDF/CSV), periodicidade e envio imediato para o e-mail do usuário.
 */
export const BackupSettings: React.FC = () => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    fetchBackupSettings()
      .then(setSettings)
      .catch((err) => setFeedback({ ok: false, msg: `Não foi possível carregar o backup automático: ${describeError(err)}` }))
      .finally(() => setLoading(false));
  }, []);

  const flash = (f: { ok: boolean; msg: string }) => {
    setFeedback(f);
    window.setTimeout(() => setFeedback(null), 8000);
  };

  const save = async (patch: Partial<Pick<Settings, 'enabled' | 'format' | 'frequency'>>) => {
    if (!settings) return;
    const previous = settings;
    setSettings({ ...settings, ...patch });
    setSaving(true);
    try {
      const updated = await updateBackupSettings(patch);
      setSettings(updated);
      flash({
        ok: true,
        msg: updated.enabled
          ? `Backup automático ativo: ${BACKUP_FREQUENCY_LABELS[updated.frequency].toLowerCase()}, em ${updated.format}, para ${updated.email}.`
          : 'Backup automático desativado.',
      });
    } catch (err) {
      setSettings(previous);
      flash({ ok: false, msg: `Não foi possível salvar: ${describeError(err)}` });
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await sendBackupNow();
      setSettings((s) => (s ? { ...s, lastSentAt: new Date().toISOString() } : s));
      flash({ ok: true, msg: `Backup enviado para ${r.to} (${r.filename}, ${(r.bytes / 1024).toFixed(0)} KB).` });
    } catch (err) {
      flash({ ok: false, msg: `Não foi possível enviar: ${describeError(err)}` });
    } finally {
      setSending(false);
    }
  };

  const fmtDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'nunca';

  return (
    <section className="pt-12 border-t dark:border-slate-800 space-y-8">
      <div className="flex items-center gap-3">
        <ShieldCheck className="text-indigo-500" size={20} />
        <h4 className="text-[11px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.3em]">Backup Automático</h4>
      </div>

      <div className="bg-slate-50 dark:bg-slate-800/40 p-5 sm:p-8 md:p-10 rounded-[2rem] sm:rounded-[3rem] border border-slate-200 dark:border-slate-800 space-y-8">
        {loading ? (
          <div className="flex items-center gap-3 text-slate-400 text-xs font-black uppercase tracking-widest">
            <Loader2 className="animate-spin" size={16} /> Carregando...
          </div>
        ) : settings ? (
          <>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
              <div className="space-y-2">
                <p className="text-lg font-black text-slate-800 dark:text-white tracking-tight">Receber backup dos dados por e-mail</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed max-w-2xl">
                  O arquivo é enviado para <strong className="text-slate-700 dark:text-slate-200">{settings.email}</strong> na periodicidade escolhida e contém {ROLE_SCOPE[settings.role] ?? 'os dados aos quais seu perfil tem acesso'}.
                </p>
                {!settings.deliveryConfigured && (
                  <p className="text-[11px] font-bold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 px-4 py-3 rounded-2xl">
                    O envio de e-mail ainda não está configurado no servidor: os backups só serão entregues depois da configuração do SMTP.
                  </p>
                )}
              </div>
              <label className="flex items-center gap-3 cursor-pointer shrink-0">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{settings.enabled ? 'Ativado' : 'Desativado'}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={settings.enabled}
                  disabled={saving}
                  onClick={() => void save({ enabled: !settings.enabled })}
                  className={`relative w-14 h-8 rounded-full transition-colors ${settings.enabled ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-700'}`}
                >
                  <span className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.enabled ? 'translate-x-6' : ''}`} />
                </button>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Formato do arquivo</label>
                <div className="flex gap-2">
                  {(['PDF', 'CSV'] as BackupFormat[]).map((f) => (
                    <button
                      key={f}
                      type="button"
                      disabled={saving}
                      onClick={() => void save({ format: f })}
                      className={`flex-1 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest border transition-all ${
                        settings.format === f
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white dark:bg-slate-900 text-slate-500 border-slate-200 dark:border-slate-700 hover:border-indigo-400'
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Periodicidade</label>
                <select
                  value={settings.frequency}
                  disabled={saving}
                  onChange={(e) => void save({ frequency: e.target.value as BackupFrequency })}
                  className="w-full p-3.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-xs font-bold dark:text-white outline-none"
                >
                  {(Object.keys(BACKUP_FREQUENCY_LABELS) as BackupFrequency[]).map((f) => (
                    <option key={f} value={f}>{BACKUP_FREQUENCY_LABELS[f]}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Último envio</label>
                <p className="p-3.5 text-xs font-bold text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl">
                  {fmtDate(settings.lastSentAt)}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <button
                type="button"
                onClick={() => void sendNow()}
                disabled={sending || !settings.deliveryConfigured}
                className="px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Enviar backup agora
              </button>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest flex items-center gap-2">
                <Mail size={12} /> O envio automático acontece de manhã, na periodicidade escolhida.
              </p>
            </div>

            {feedback && (
              <div
                role="status"
                className={`px-5 py-4 rounded-2xl text-xs font-bold ${
                  feedback.ok
                    ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-900/50'
                    : 'bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 border border-rose-100 dark:border-rose-900/50'
                }`}
              >
                {feedback.msg}
              </div>
            )}
          </>
        ) : (
          feedback && <p className="text-xs font-bold text-rose-600">{feedback.msg}</p>
        )}
      </div>
    </section>
  );
};

export default BackupSettings;
